import * as React from "react";
import { Upload, notification } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import { InfoTooltip } from "../InfoTooltip";

export interface ImportButtonStandaloneProps {
  onImport: (content: string, fileName: string) => void;
  accept?: string;
  explanation?: React.ReactNode;
  existingHasContent?: boolean;
  label?: string;
}

export function ImportButtonStandalone({
  onImport,
  accept = "*",
  explanation,
  existingHasContent = false,
  label = "Drop file or click to import",
}: ImportButtonStandaloneProps) {
  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounter = React.useRef(0);

  const handleImport = React.useCallback(
    async (file: File) => {
      try {
        const content = await file.text();

        if (!content || content.trim().length === 0) {
          notification.error({ message: "File is empty" });
          return;
        }

        onImport(content, file.name);
        notification.success({
          message: existingHasContent ? "File imported (previous content replaced)" : "File imported successfully",
        });
      } catch (e) {
        notification.error({ message: `Failed to read file: ${(e as Error).message}` });
      }
    },
    [onImport, existingHasContent]
  );

  const uploadProps = {
    showUploadList: false,
    accept,
    beforeUpload: async (file: File) => {
      await handleImport(file);
      return false as const;
    },
  };

  return (
    <div className="inline-flex items-center">
      <Upload {...uploadProps} className="inline-block [&_.ant-upload]:block">
        <div
          className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded cursor-pointer transition-all text-sm"
          style={{
            borderColor: isDragOver ? "var(--color-primary)" : "var(--color-border)",
            color: isDragOver ? "var(--color-primary)" : "var(--color-label)",
            backgroundColor: isDragOver ? "var(--color-primary-faint)" : "transparent",
          }}
          onMouseEnter={(e) => {
            if (!isDragOver) {
              e.currentTarget.style.borderColor = "var(--color-primary)";
              e.currentTarget.style.color = "var(--color-primary)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragOver) {
              e.currentTarget.style.borderColor = "var(--color-border)";
              e.currentTarget.style.color = "var(--color-label)";
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            dragCounter.current++;
            setIsDragOver(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => {
            dragCounter.current--;
            if (dragCounter.current === 0) setIsDragOver(false);
          }}
          onDrop={() => {
            dragCounter.current = 0;
            setIsDragOver(false);
          }}
        >
          <ImportOutlined />
          <span>{label}</span>
        </div>
      </Upload>
      {explanation && <InfoTooltip title={explanation} style={{ marginLeft: 8 }} />}
    </div>
  );
}
